import React, { createContext, useEffect, useState, ReactNode, useCallback } from 'react';
import posthog from 'posthog-js';

// PostHog configuration - will be moved to Vercel env variables in production
const POSTHOG_KEY = 'phc_XALIrehSGRMNTNQ5CuqPyrBsFftsX0oL0hIFaEgMdL1';
const POSTHOG_HOST = 'https://us.i.posthog.com';

interface PostHogContextValue {
  posthog: typeof posthog | null;
  isInitialized: boolean;
}

export const PostHogContext = createContext<PostHogContextValue>({
  posthog: null,
  isInitialized: false,
});

// Check if analytics consent has been given via Termly
function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    // Check Termly consent cookie
    const termlyConsent = document.cookie
      .split('; ')
      .find(row => row.startsWith('termly_consent='));
    
    if (termlyConsent) {
      const consentValue = decodeURIComponent(termlyConsent.split('=')[1]);
      const consent = JSON.parse(consentValue);
      // Check if analytics category is consented
      return consent?.analytics === true;
    }
    
    return false;
  } catch (error) {
    console.warn('Error checking Termly consent:', error);
    return false;
  }
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);

  const initializePostHog = useCallback(() => {
    // Don't initialize if already done
    if (isInitialized) return;
    
    try {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: true,
        capture_pageview: false, // We'll handle this manually for SSR compatibility
        capture_pageleave: true,
        persistence: 'localStorage',
        respect_dnt: true,
        disable_session_recording: true, // Can be enabled later if needed
        loaded: () => {
          console.log('PostHog initialized successfully');
          setIsInitialized(true);
        },
        defaults: '2025-05-24',
      });
    } catch (error) {
      console.error('PostHog initialization error:', error);
    }
  }, [isInitialized]);

  useEffect(() => {
    // Only run on client-side
    if (typeof window === 'undefined') return;

    // Check if consent is already given
    const checkAndInitialize = () => {
      if (hasAnalyticsConsent()) {
        if (window.requestIdleCallback) {
          requestIdleCallback(initializePostHog, { timeout: 2000 });
        } else {
          setTimeout(initializePostHog, 1000);
        }
      }
    };

    // Initial check
    checkAndInitialize();

    // Listen for Termly consent changes
    const handleConsentChange = () => {
      if (hasAnalyticsConsent() && !isInitialized) {
        initializePostHog();
      }
    };

    // Termly dispatches a custom event when consent changes
    window.addEventListener('termly:consent', handleConsentChange);
    
    // Also poll for consent changes as a fallback (Termly may not fire events consistently)
    const pollInterval = setInterval(() => {
      if (hasAnalyticsConsent() && !isInitialized) {
        initializePostHog();
        clearInterval(pollInterval);
      }
    }, 1000);

    // Stop polling after 30 seconds to avoid unnecessary CPU usage
    const pollTimeout = setTimeout(() => {
      clearInterval(pollInterval);
    }, 30000);

    return () => {
      window.removeEventListener('termly:consent', handleConsentChange);
      clearInterval(pollInterval);
      clearTimeout(pollTimeout);
    };
  }, [initializePostHog, isInitialized]);

  return (
    <PostHogContext.Provider value={{ posthog: isInitialized ? posthog : null, isInitialized }}>
      {children}
    </PostHogContext.Provider>
  );
}
